import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { isFunction, isString } from '@impuls-coach/util-is';
import Icon from 'components/atoms/Icon';
import noop from 'helper/noop';

export type LinkProps = {
	modifier?: string;
	openInNewWindow?: boolean;
	iconLeft?: string;
	title?: string;
	href?: string;
	externalHref?: string;
	iconRight?: string | null;
	disabled?: boolean;
	loading?: boolean;
	withoutIcon?: boolean;
	buttonBarId?: string; // used for button bar identification handler
	onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export type CleanAttrsType = {
	to?: string;
	[key: string]: any;
};

const Link = ({
	modifier,
	href,
	openInNewWindow = false,
	iconLeft,
	title,
	onClick,
	externalHref,
	iconRight,
	buttonBarId,
	withoutIcon = false,
	disabled = false,
	loading = false,
}: LinkProps) => {
	if (!withoutIcon && (!iconRight || iconRight === null) && iconRight !== '') {
		iconRight = 'link-arrow';
	}

	function isValidIcon(icon?: string): boolean {
		return isString(icon) && icon !== '';
	}

	function className(): string {
		const out: Array<string> = ['a-nexus'];
		if (modifier) {
			out.push(`a-nexus--${modifier}`);
		}
		if (disabled) {
			out.push('state-a-nexus--disabled');
		}
		if (loading) {
			out.push('state-a-nexus--loading');
		}
		return out.join(' ');
	}

	if (isString(externalHref) && !isFunction(onClick)) {
		const cleanAttrs: CleanAttrsType = {
			href: externalHref || '',
		};

		if (openInNewWindow) {
			cleanAttrs.target = '_blank';
		}

		return (
			<a className={className()} {...cleanAttrs}>
				{isValidIcon(iconLeft) && (
					<span className="a-nexus__icon-left">
						<Icon iconName={iconLeft} />
					</span>
				)}

				<span className="a-nexus__text">
					{title}
					{!withoutIcon && iconRight && isValidIcon(iconRight) && (
						<span className="a-nexus__icon-right">
							<Icon iconName={iconRight} />
						</span>
					)}
				</span>
			</a>
		);
	} else if (isString(href)) {
		const cleanAttrs: CleanAttrsType = {
			to: href || '',
		};

		if (openInNewWindow) {
			cleanAttrs.target = '_blank';
		}

		if (isFunction(onClick) && !loading && !disabled) {
			cleanAttrs.onClick = onClick;
		}

		return (
			<RouterLink className={className()} data-router-link {...(cleanAttrs as any)}>
				{isValidIcon(iconLeft) && (
					<span className="a-nexus__icon-left">
						<Icon iconName={iconLeft} />
					</span>
				)}

				<span className="a-nexus__text">
					{title}
					{iconRight && isValidIcon(iconRight) && (
						<span className="a-nexus__icon-right">
							<Icon iconName={iconRight} />
						</span>
					)}
				</span>
			</RouterLink>
		);
	}

	return (
		<a
			className={className()}
			onClick={
				isFunction(onClick) && !loading && !disabled
					? (...args: any) => onClick && onClick(buttonBarId || args)
					: noop
			}
		>
			{isValidIcon(iconLeft) && (
				<span className="a-nexus__icon-left">
					<Icon iconName={iconLeft} />
				</span>
			)}

			<span className="a-nexus__text">
				{title}
				{iconRight && isValidIcon(iconRight) && (
					<span className="a-nexus__icon-right">
						<Icon iconName={iconRight} />
					</span>
				)}
			</span>
		</a>
	);
};

export default Link;
